/**
 * incomeEngine.ts
 *
 * 【役割】純粋な所得金額の計算
 *   - 税率・税額の適用は一切行わない
 *   - 所得税 / 住民税 / 社会保険料など、どの税種エンジンからでも再利用できる
 *
 * 【呼び出し元】
 *   - nationalTaxEngine.ts（所得税）
 *   - 将来: residentTaxEngine.ts（住民税）
 *   - 将来: socialInsuranceEngine.ts（社会保険料）
 *
 * 【注意】
 *   - 青色申告特別控除は「所得の計算」に影響するためここに含める
 *     （控除額を不動産→事業→山林の順に消費する）
 *   - 公的年金等控除などの「所得控除」のうち、所得金額そのものを変えるものもここ
 *   - 基礎控除・勤労学生控除など「課税所得 = 総所得 - 控除」の部分は各エンジン側
 */

import type {
  一時所得計算のための情報,
  不動産所得計算のための情報,
  事業所得計算のための情報,
  不動産の譲渡所得計算のための情報,
  不動産以外の譲渡所得計算のための情報,
  雑所得計算のための情報,
  山林所得計算のための情報,
} from './taxCalcInput';

// ---------------------------------------------------------------------------
// 給与所得
// ---------------------------------------------------------------------------

/**
 * 給与所得 = 給与収入 - 給与所得控除（69万）- 特例控除（5万）
 * ※ 2025年税制改正（令和7年度）で給与所得控除の特例5万円が恒久化された想定
 */
export function 給与所得計算関数(給与収入: number): number {
  const 給与所得控除 = 690000;
  const 給与所得控除の特例 = 50000;
  return Math.max(0, 給与収入 - 給与所得控除 - 給与所得控除の特例);
}

// ---------------------------------------------------------------------------
// 一時所得
// ---------------------------------------------------------------------------

/**
 * 一時所得 = max(0, 一時収入 - 経費 - 50万円特別控除)
 * ※ 総所得算入時は 1/2 する（算入処理は呼び出し元エンジン側）
 */
export function 一時所得計算関数(input: 一時所得計算のための情報): number {
  return Math.max(0, input.一時収入 - input.一時収入にかかった経費 - 500_000);
}

// ---------------------------------------------------------------------------
// 雑所得
// ---------------------------------------------------------------------------

/**
 * 雑所得 = 公的年金等雑所得 + その他雑所得
 *
 * 公的年金等控除（簡易モデル）:
 *   65歳以上 → 110万円（最低ライン）
 *   65歳未満 → 60万円（最低ライン）
 * ※ 実際は年金収入額に応じた速算表があるが、ここでは最低控除のみのイメージ
 */
export function 雑所得計算関数(input: 雑所得計算のための情報, 年齢: number): number {
  const 公的年金等控除額 = 年齢 >= 65 ? 1_100_000 : 600_000;
  const 公的年金等雑所得 = Math.max(0, input.公的老齢年金収入 - 公的年金等控除額);
  const その他雑所得 = Math.max(0, input.その他雑収入 - input.その他雑収入にかかった経費);
  return 公的年金等雑所得 + その他雑所得;
}

// ---------------------------------------------------------------------------
// 不動産所得（青色申告控除枠を消費して次へバトンパス）
// ---------------------------------------------------------------------------

export type 不動産所得計算結果 = {
  不動産所得: number;
  /** 残った青色申告特別控除枠（次の事業所得計算へ渡す） */
  不動産所得を考慮後の青色申告控除額: number;
};

/**
 * 不動産所得 = (家賃収入 + 礼金更新料) - 必要経費 - 青色申告特別控除（残枠まで）
 * 赤字の場合は控除を使わずそのまま返す（損益通算のため）
 */
export function 不動産所得計算関数(
  input: 不動産所得計算のための情報,
  青色申告控除枠: number,
): 不動産所得計算結果 {
  const 総収入 = input.家賃収入 + input.礼金更新料;
  const 差し引き金額 = 総収入 - input.必要経費;

  if (差し引き金額 <= 0) {
    return { 不動産所得: 差し引き金額, 不動産所得を考慮後の青色申告控除額: 青色申告控除枠 };
  }

  const 今回使った控除額 = Math.min(差し引き金額, 青色申告控除枠);
  return {
    不動産所得: 差し引き金額 - 今回使った控除額,
    不動産所得を考慮後の青色申告控除額: 青色申告控除枠 - 今回使った控除額,
  };
}

// ---------------------------------------------------------------------------
// 事業所得（青色申告控除枠の残りを消費して次へバトンパス）
// ---------------------------------------------------------------------------

export type 事業所得計算結果 = {
  事業所得: number;
  /** 残った青色申告特別控除枠（次の山林所得計算へ渡す） */
  不動産所得と事業所得を考慮後の青色申告控除額: number;
};

/**
 * 事業所得 = 総収入 - 必要経費 - 青色申告特別控除（残枠まで）
 * 赤字の場合は控除を使わずそのまま返す（損益通算のため）
 */
export function 事業所得計算関数(
  input: 事業所得計算のための情報,
  青色申告控除額: number,
): 事業所得計算結果 {
  const 差し引き金額 = input.総収入金額 - input.必要経費;

  if (差し引き金額 <= 0) {
    return {
      事業所得: 差し引き金額,
      不動産所得と事業所得を考慮後の青色申告控除額: 青色申告控除額,
    };
  }

  const 今回使う控除額 = Math.min(差し引き金額, 青色申告控除額);
  return {
    事業所得: 差し引き金額 - 今回使う控除額,
    不動産所得と事業所得を考慮後の青色申告控除額: 青色申告控除額 - 今回使う控除額,
  };
}

// ---------------------------------------------------------------------------
// 不動産以外の譲渡所得（総合課税扱い）
// ---------------------------------------------------------------------------

/**
 * 不動産以外の譲渡所得（ゴルフ会員権など）
 *   = max(0, 利益 - 50万円特別控除) → 長期(5年超)なら さらに 1/2
 * ※ 総所得への算入は1/2後の金額で行う（一時所得と異なり算入時の1/2ではなく計算時の1/2）
 */
export function 不動産以外の譲渡所得計算関数(
  input: 不動産以外の譲渡所得計算のための情報,
): number {
  const 利益 = input.譲渡価額 - (input.取得費 + input.譲渡費用);
  let 譲渡所得 = Math.max(0, 利益 - 500_000);
  if (input.所有期間は5年超か) 譲渡所得 = 譲渡所得 / 2;
  return 譲渡所得;
}

// ---------------------------------------------------------------------------
// 山林所得（分離課税）の所得金額のみ
// ---------------------------------------------------------------------------

/**
 * 山林所得 = 山林収入 - 山林必要経費 - 山林特別控除(最高50万) - 青色申告特別控除(最高10万)
 * 税額計算（5分5乗方式）は nationalTaxEngine 側
 */
export function 山林所得計算関数(
  input: 山林所得計算のための情報,
  青色申告控除額: number,
): { 山林所得: number } {
  const 差し引き金額 = input.山林収入 - input.山林必要経費;

  if (差し引き金額 <= 0) return { 山林所得: 差し引き金額 };

  const 山林特別控除額 = Math.min(差し引き金額, 500_000);
  const 特別控除後 = 差し引き金額 - 山林特別控除額;

  // 山林に回ってくる青色枠は最大10万円まで
  const 今回使う青色控除額 = Math.min(特別控除後, Math.min(100_000, 青色申告控除額));
  return { 山林所得: 特別控除後 - 今回使う青色控除額 };
}

// ---------------------------------------------------------------------------
// 不動産の譲渡所得（分離課税）の所得金額のみ
// ---------------------------------------------------------------------------

/**
 * 不動産の譲渡所得 = max(0, 譲渡価額 - (取得費 + 譲渡費用))
 * 税率（15.315% / 30.63%）の適用は nationalTaxEngine 側
 */
export function 不動産の譲渡所得計算関数(
  input: 不動産の譲渡所得計算のための情報,
): { 不動産の譲渡所得: number } {
  return {
    不動産の譲渡所得: Math.max(0, input.譲渡価額 - (input.取得費 + input.譲渡費用)),
  };
}
